import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import LoginApi from "../services/LoginApiClient";
import {
  setupApiInterceptors,
  cleanupApiInterceptors,
} from "../services/apiInterceptor";

interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  role?: string;
  isActive?: boolean;
  businesses?: Business[];
  SelectedBusiness?: Business;
}

interface Business {
  id: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  changeSelectedBusiness: (business: Business) => void;
  updateUserProfile: (profileData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    console.log("User logged out successfully");
  };

  useEffect(() => {
    // Setup API interceptors for 401 handling
    setupApiInterceptors(() => {
      // Handle unauthorized access
      logout();
      // Use window.location for redirect since we can't use useNavigate here
      window.location.href = "/login";
    });

    // Check if user is already logged in from localStorage
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      }
    }
    setLoading(false);

    // Cleanup interceptors on unmount
    return () => {
      cleanupApiInterceptors();
    };
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await LoginApi.login(username, password);

      // Create user object from response
      const businesses =
        response.data.businesses?.map((b: any) => ({
          id: b.id,
          name: b.name,
        })) || [];

      const userData = {
        id: response.data.id,
        username: username,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email,
        phone: response.data.phone,
        isActive: response.data.isActive,
        role: response.data.role,
        businesses,
        SelectedBusiness: businesses.length > 0 ? businesses[0] : undefined,
      };

      console.log("Created userData:", userData);

      // Check if user role is "POS" and fail login if so
      if (userData.role === "POS") {
        throw new Error("POS users are not allowed to login to this application");
      }

      // Save user data and token/session info
      localStorage.setItem("user", JSON.stringify(userData));
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
      }

      setUser(userData);
      console.log("User set successfully:", userData);
    } catch (error: any) {
      throw error; // Re-throw the error from LoginApi
    }
  };

  const changeSelectedBusiness = (business: Business) => {
    if (user) {
      const updatedUser = {
        ...user,
        SelectedBusiness: business,
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const updateUserProfile = (profileData: Partial<User>) => {
    if (user) {
      const updatedUser = {
        ...user,
        ...profileData,
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    changeSelectedBusiness,
    updateUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
